import { supabase } from '@/integrations/supabase/client';
import { devLog } from '@/utils/logger';
import type { 
  LeavePolicy, 
  LeavePolicyWithRules,
  LeaveAccrualRule,
  LeaveBalance,
  LeaveBalanceWithProfile,
  PolicyEmployeeAssignment,
  CreateLeavePolicyInput,
  UpdateLeavePolicyInput,
  AssignPolicyToEmployeeInput,
  UpdateLeaveBalanceInput
} from './leavePolicyTypes';
import type { Profile } from './types';
import { policyDependencyService, type PolicyDependencyReport } from './policyDependencyService';

const handleLeavePolicyError = (error: any, operation: string) => {
  console.error(`[Leave Policy Service] ${operation} failed:`, error);
  
  if (error.code === 'PGRST116') {
    throw new Error('Policy not found');
  }
  if (error.code === '42501') {
    throw new Error('Insufficient permissions');
  }
  if (error.code === '23505') {
    // Distinguish between policy name conflicts and assignment conflicts
    if (operation.includes('assign') || operation.includes('assignment')) {
      throw new Error('Employee is already assigned to this policy. Please check existing assignments.');
    }
    throw new Error('Policy name already exists');
  }
  
  // Handle duplicate key errors during assignment operations
  if (error.code === '42P10' && (operation.includes('assign') || operation.includes('assignment'))) {
    throw new Error('Unable to assign employee to policy. This may be due to existing assignment data.');
  }
  
  throw new Error(error.message || `Failed to ${operation}`);
};

// Helper function to sanitize date fields - convert empty strings to null
const sanitizeDateFields = (data: any): any => {
  const sanitized = { ...data };
  
  // List of date fields that might be empty strings
  const dateFields = [
    'pro_rata_start_date',
    'fixed_accrual_specific_date',
    
    'rollover_specific_date'
  ];
  
  dateFields.forEach(field => {
    if (sanitized[field] === '') {
      sanitized[field] = null;
    }
  });
  
  return sanitized;
};

export const leavePolicyService = {
  // Policy CRUD operations
  async getLeavePolicies(): Promise<LeavePolicyWithRules[]> {
    try {
      const { data, error } = await supabase
        .from('leave_policies')
        .select(`
          *,
          accrual_rule:leave_accrual_rules!policy_id(*),
          approvers:leave_policy_approvers!policy_id(
            id,
            policy_id,
            company_id,
            approver_profile_id,
            approval_order,
            can_override_policy,
            created_at,
            updated_at
          ),
          assignments:policy_employee_assignments!policy_id(
            id,
            is_active
          )
         `)
        // Commenting out this filter to get all policies (active and inactive)
        // .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Process the data to handle potential missing relationships
      const processedData = (data || []).map(policy => {
        const assignments = Array.isArray(policy.assignments) ? policy.assignments : [];
        const activeAssignmentsCount = assignments.filter((a: any) => a.is_active).length;
        
        return {
          ...policy,
          accrual_rule: Array.isArray(policy.accrual_rule) ? policy.accrual_rule[0] : policy.accrual_rule,
          approvers: Array.isArray(policy.approvers) ? policy.approvers : [],
          assigned_employees_count: activeAssignmentsCount
        };
      }) as LeavePolicyWithRules[];
      
      return processedData;
    } catch (error) {
      handleLeavePolicyError(error, 'get leave policies');
      throw error;
    }
  },

  async getLeavePolicyById(id: string): Promise<LeavePolicyWithRules> {
    try {
      const { data, error } = await supabase
        .from('leave_policies')
        .select(`
          *,
          accrual_rule:leave_accrual_rules!policy_id(*),
          approvers:leave_policy_approvers!policy_id(
            id,
            policy_id,
            company_id,
            approver_profile_id,
            approval_order,
            can_override_policy,
            created_at,
            updated_at
          ),
          assignments:policy_employee_assignments!policy_id(
            id,
            is_active
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      
      // Process the data to handle potential missing relationships
      const assignments = Array.isArray(data.assignments) ? data.assignments : [];
      const activeAssignmentsCount = assignments.filter((a: any) => a.is_active).length;
      
      const processedData = {
        ...data,
        accrual_rule: Array.isArray(data.accrual_rule) ? data.accrual_rule[0] : data.accrual_rule,
        approvers: Array.isArray(data.approvers) ? data.approvers : [],
        assigned_employees_count: activeAssignmentsCount
      } as LeavePolicyWithRules;
      
      return processedData;
    } catch (error) {
      handleLeavePolicyError(error, 'get leave policy');
      throw error;
    }
  },

  async createLeavePolicy(input: CreateLeavePolicyInput): Promise<LeavePolicy> {
    try {
      // Get current user's profile and company
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, company_id')
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (!profile) throw new Error('User profile not found');

      // Create the policy
      const { data: policy, error: policyError } = await supabase
        .from('leave_policies')
        .insert([{
          company_id: profile.company_id,
          name: input.name,
          description: input.description,
          leave_type: input.leave_type,
          accrual_method: input.accrual_method,
          created_by: profile.id
        }])
        .select()
        .single();

      if (policyError) throw policyError;

      // Create the accrual rule
      const accrualRuleData = sanitizeDateFields({
        policy_id: policy.id,
        company_id: profile.company_id,
        ...input.accrual_rule,
        accrual_rate_tiers: input.accrual_rule.accrual_rate_tiers ? JSON.stringify(input.accrual_rule.accrual_rate_tiers) : '[]',
        progressive_caps: input.accrual_rule.progressive_caps ? JSON.stringify(input.accrual_rule.progressive_caps) : '[]',
        rolling_cap_periods: input.accrual_rule.rolling_cap_periods ? JSON.stringify(input.accrual_rule.rolling_cap_periods) : '[]'
      });

      const { error: ruleError } = await supabase
        .from('leave_accrual_rules')
        .insert([accrualRuleData]);

      if (ruleError) throw ruleError;

      // Add approvers if specified
      if (input.approver_profile_ids?.length) {
        const approvers = input.approver_profile_ids.map((approver_id, index) => ({
          policy_id: policy.id,
          company_id: profile.company_id,
          approver_profile_id: approver_id,
          approval_order: index + 1
        }));

        const { error: approverError } = await supabase
          .from('leave_policy_approvers')
          .insert(approvers);

        if (approverError) throw approverError;
      }

      return policy as LeavePolicy;
    } catch (error) {
      handleLeavePolicyError(error, 'create leave policy');
      throw error;
    }
  },

  async updateLeavePolicy(id: string, input: UpdateLeavePolicyInput): Promise<{ policyUpdated: true; balancesRecalculated: boolean; balanceError?: string; employeesUpdated: number }> {
    try {
      devLog('[Leave Policy Service] Starting policy update:', { 
        policyId: id, 
        input,
        timestamp: new Date().toISOString()
      });

      // Validate input data
      if (!input.name || !input.leave_type || !input.accrual_method) {
        throw new Error('Missing required fields: name, leave_type, or accrual_method');
      }

      // Get current user's profile for company context
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, company_id')
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (!profile) throw new Error('User profile not found');
      
      devLog('[Leave Policy Service] User profile:', profile);

      // Check if policy exists and user has access
      const { data: existingPolicy } = await supabase
        .from('leave_policies')
        .select('id, company_id, name')
        .eq('id', id)
        .eq('company_id', profile.company_id)
        .single();

      if (!existingPolicy) {
        throw new Error('Policy not found or access denied');
      }
      
      devLog('[Leave Policy Service] Existing policy:', existingPolicy);

      // Prepare update data with explicit field filtering
      const policyUpdateData = {
        name: input.name,
        description: input.description || null,
        leave_type: input.leave_type,
        accrual_method: input.accrual_method,
        is_active: input.is_active !== undefined ? input.is_active : true,
        updated_at: new Date().toISOString()
      };

      devLog('[Leave Policy Service] Policy update data:', policyUpdateData);

      // Update main policy
      const { error: policyError, data: updatedPolicy } = await supabase
        .from('leave_policies')
        .update(policyUpdateData)
        .eq('id', id)
        .eq('company_id', profile.company_id)
        .select()
        .single();

      if (policyError) {
        console.error('[Leave Policy Service] Policy update error:', policyError);
        throw policyError;
      }
      
      devLog('[Leave Policy Service] Policy updated successfully:', updatedPolicy);

      // Update accrual rule if provided
      if (input.accrual_rule) {
        devLog('[Leave Policy Service] Updating accrual rule:', input.accrual_rule);
        
        // Sanitize date fields to convert empty strings to null
        const sanitizedAccrualRule = sanitizeDateFields(input.accrual_rule);
        
        // Filter out undefined values and prepare accrual rule data
        const accrualRuleUpdate: any = {};
        
        // Only include fields that are not undefined
        Object.keys(sanitizedAccrualRule).forEach(key => {
          const value = (sanitizedAccrualRule as any)[key];
          if (value !== undefined) {
            accrualRuleUpdate[key] = value;
          }
        });

        accrualRuleUpdate.updated_at = new Date().toISOString();

        devLog('[Leave Policy Service] Accrual rule update data:', accrualRuleUpdate);

        const { error: ruleError, data: updatedRule } = await supabase
          .from('leave_accrual_rules')
          .update(accrualRuleUpdate)
          .eq('policy_id', id)
          .eq('company_id', profile.company_id)
          .select()
          .single();

        if (ruleError) {
          console.error('[Leave Policy Service] Accrual rule update error:', ruleError);
          throw ruleError;
        }
        
        devLog('[Leave Policy Service] Accrual rule updated successfully:', updatedRule);
      }

      // Handle approver updates if provided (including empty array to clear approvers)
      if (input.approver_profile_ids !== undefined) {
        devLog('[Leave Policy Service] Updating approvers:', input.approver_profile_ids);
        
        // Delete existing approvers
        const { error: deleteError } = await supabase
          .from('leave_policy_approvers')
          .delete()
          .eq('policy_id', id)
          .eq('company_id', profile.company_id);

        if (deleteError) {
          console.error('[Leave Policy Service] Delete approvers error:', deleteError);
          throw deleteError;
        }

        devLog('[Leave Policy Service] Existing approvers deleted');

        // Insert new approvers if any provided
        if (input.approver_profile_ids.length > 0) {
          const approvers = input.approver_profile_ids.map((approver_id, index) => ({
            policy_id: id,
            company_id: profile.company_id,
            approver_profile_id: approver_id,
            approval_order: index + 1,
            can_override_policy: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }));

          devLog('[Leave Policy Service] Inserting new approvers:', approvers);

          const { error: approverError, data: insertedApprovers } = await supabase
            .from('leave_policy_approvers')
            .insert(approvers)
            .select();

          if (approverError) {
            console.error('[Leave Policy Service] Insert approvers error:', approverError);
            throw approverError;
          }
          
          devLog('[Leave Policy Service] New approvers inserted:', insertedApprovers);
        }
      }

      devLog('[Leave Policy Service] Policy update completed successfully at:', new Date().toISOString());
      
      // Recalculate balances for all employees assigned to this policy
      devLog('[Leave Policy Service] Recalculating employee balances for policy:', id);
      const { data: updatedCount, error: balanceError } = await supabase.rpc('bulk_update_policy_balances', {
        p_policy_id: id
      });

      if (balanceError) {
        console.error('[Leave Policy Service] Balance recalculation error:', balanceError);
        // Don't throw — policy update succeeded. Surface the failure to the caller.
        return {
          policyUpdated: true,
          balancesRecalculated: false,
          balanceError: balanceError.message,
          employeesUpdated: 0,
        };
      }

      devLog(`[Leave Policy Service] Successfully recalculated balances for ${updatedCount || 0} employees`);
      return {
        policyUpdated: true,
        balancesRecalculated: true,
        employeesUpdated: updatedCount || 0,
      };
    } catch (error) {
      console.error('[Leave Policy Service] Update failed with error:', error);
      handleLeavePolicyError(error, 'update leave policy');
      throw error;
    }
  },

  /**
   * Check policy dependencies before deletion
   */
  async checkPolicyDependencies(policyId: string): Promise<PolicyDependencyReport> {
    return policyDependencyService.checkPolicyDependencies(policyId);
  },

  /**
   * Delete (deactivate) a leave policy with dependency checking
   * @param id - Policy ID to delete
   * @param options - Deletion options
   */
  async deleteLeavePolicy(
    id: string, 
    options: { 
      force?: boolean; 
      cascadeDeactivate?: boolean; 
    } = {}
  ): Promise<PolicyDependencyReport> {
    try {
      // First check dependencies
      const dependencyReport = await this.checkPolicyDependencies(id);
      
      // If policy has blocking dependencies and force is not enabled, prevent deletion
      if (dependencyReport.blockingDependencies > 0 && !options.force) {
        const message = policyDependencyService.generateDependencyMessage(dependencyReport);
        throw new Error(message);
      }

      // If cascadeDeactivate is enabled, deactivate related assignments
      if (options.cascadeDeactivate && dependencyReport.dependencies.activeAssignments.count > 0) {
        const { error: assignmentError } = await supabase
          .from('policy_employee_assignments')
          .update({ is_active: false, end_date: new Date().toISOString().split('T')[0] })
          .eq('policy_id', id)
          .eq('is_active', true);

        if (assignmentError) {
          throw new Error(`Failed to deactivate policy assignments: ${assignmentError.message}`);
        }
      }

      // Perform the soft delete - set policy inactive
      const { error } = await supabase
        .from('leave_policies')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;

      // Return updated dependency report
      return {
        ...dependencyReport,
        canDelete: true // Policy has been successfully deactivated
      };
    } catch (error) {
      console.error('[Leave Policy Service] Delete policy failed:', error);
      handleLeavePolicyError(error, 'delete leave policy');
      throw error;
    }
  },

  /**
   * Reactivate a deactivated leave policy
   * @param id - Policy ID to reactivate
   */
  async reactivateLeavePolicy(id: string): Promise<void> {
    try {
      // Get current user's profile for company context
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, company_id')
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (!profile) throw new Error('User profile not found');

      // Check if policy exists and is inactive
      const { data: existingPolicy } = await supabase
        .from('leave_policies')
        .select('id, company_id, name, is_active')
        .eq('id', id)
        .eq('company_id', profile.company_id)
        .single();

      if (!existingPolicy) {
        throw new Error('Policy not found or access denied');
      }

      if (existingPolicy.is_active) {
        throw new Error('Policy is already active');
      }

      // Reactivate the policy
      const { error } = await supabase
        .from('leave_policies')
        .update({ is_active: true, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('company_id', profile.company_id);

      if (error) throw error;
    } catch (error) {
      console.error('[Leave Policy Service] Reactivate policy failed:', error);
      handleLeavePolicyError(error, 'reactivate leave policy');
      throw error;
    }
  },

  /**
   * Permanently delete a leave policy (hard delete)
   * Only allowed for inactive policies with no blocking dependencies
   */
  async permanentlyDeleteLeavePolicy(id: string): Promise<void> {
    try {
      // Use the atomic server-side RPC that handles all dependent records
      // (accrual_history, processing logs, thresholds, balance_expirations, etc.)
      // and validates permissions, company ownership, and blocking dependencies.
      const { data, error } = await supabase.rpc('hard_delete_leave_policy', {
        p_policy_id: id
      });

      if (error) {
        console.error('[Leave Policy Service] Permanent deletion RPC failed:', error);
        throw new Error(error.message);
      }

      devLog('[Leave Policy Service] Policy permanently deleted successfully:', id, data);
    } catch (error) {
      console.error('[Leave Policy Service] Permanent deletion failed:', error);
      handleLeavePolicyError(error, 'permanently delete leave policy');
      throw error;
    }
  },

  // Employee assignment operations
  async assignPolicyToEmployees(input: AssignPolicyToEmployeeInput): Promise<void> {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, company_id')
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (!profile) throw new Error('User profile not found');

      // Use upsert to handle reactivating previously removed assignments
      for (const profile_id of input.profile_ids) {
        try {
          const { error } = await supabase
            .from('policy_employee_assignments')
            .upsert({
              policy_id: input.policy_id,
              profile_id,
              company_id: profile.company_id,
              start_date: input.start_date || new Date().toISOString().split('T')[0],
              is_active: true,
              end_date: null, // Clear end_date when reactivating
              assigned_by: profile.id,
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'policy_id,profile_id'
            });

          if (error) throw error;

        } catch (upsertError: any) {
          // Fallback: Manual reactivation if upsert fails
          if (upsertError.code === '42P10' || upsertError.code === '23505') {
            devLog('[leavePolicyService] Upsert failed, attempting manual reactivation for profile:', profile_id);
            
            // Try to update existing inactive assignment
            const { error: updateError } = await supabase
              .from('policy_employee_assignments')
              .update({
                is_active: true,
                end_date: null,
                start_date: input.start_date || new Date().toISOString().split('T')[0],
                assigned_by: profile.id,
                updated_at: new Date().toISOString()
              })
              .eq('policy_id', input.policy_id)
              .eq('profile_id', profile_id);

            if (updateError) {
              console.error('[leavePolicyService] Manual reactivation failed:', updateError);
              throw updateError;
            }
          } else {
            throw upsertError;
          }
        }

        // Explicitly recalculate balance for reactivated assignments
        try {
          await supabase.rpc('update_employee_leave_balance', {
            p_policy_id: input.policy_id,
            p_profile_id: profile_id
          });
        } catch (balanceError) {
          console.warn('[leavePolicyService] Failed to update balance for profile:', profile_id, balanceError);
        }
      }

      devLog('[leavePolicyService] Policy assignments upserted - balances recalculated');
    } catch (error) {
      handleLeavePolicyError(error, 'assign policy to employees');
      throw error;
    }
  },

  async getPolicyAssignments(policyId: string): Promise<PolicyEmployeeAssignment[]> {
    try {
      const { data, error } = await supabase
        .from('policy_employee_assignments')
        .select('*')
        .eq('policy_id', policyId)
        .eq('is_active', true)
        .order('start_date', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleLeavePolicyError(error, 'get policy assignments');
      throw error;
    }
  },

  async removePolicyAssignment(assignmentId: string): Promise<{ policy_id: string }> {
    try {
      // Get current user's profile for security checks
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, company_id')
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (!profile) throw new Error('User profile not found');

      // First get the assignment to retrieve policy_id before updating
      const { data: assignment, error: fetchError } = await supabase
        .from('policy_employee_assignments')
        .select('policy_id')
        .eq('id', assignmentId)
        .eq('company_id', profile.company_id)
        .single();

      if (fetchError) throw fetchError;
      if (!assignment) throw new Error('Assignment not found');

      // Soft delete (deactivate) the assignment
      const { error } = await supabase
        .from('policy_employee_assignments')
        .update({ 
          is_active: false, 
          end_date: new Date().toISOString().split('T')[0] 
        })
        .eq('id', assignmentId)
        .eq('company_id', profile.company_id);

      if (error) throw error;

      // Clean up fixed accrual release schedule so re-assignment gets fresh initialization
      const { error: releaseCleanupError } = await supabase
        .from('fixed_accrual_releases')
        .delete()
        .eq('assignment_id', assignmentId)
        .eq('company_id', profile.company_id);

      if (releaseCleanupError) {
        console.warn('[leavePolicyService] Failed to clean up fixed accrual releases for assignment:', assignmentId, releaseCleanupError);
      }
      
      return { policy_id: assignment.policy_id };
    } catch (error) {
      handleLeavePolicyError(error, 'remove policy assignment');
      throw error;
    }
  },

  async removePolicyAssignments(assignmentIds: string[]): Promise<{ policy_id: string }[]> {
    try {
      // Get current user's profile for security checks
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, company_id')
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (!profile) throw new Error('User profile not found');

      // First get the assignments to retrieve policy_ids before updating
      const { data: assignments, error: fetchError } = await supabase
        .from('policy_employee_assignments')
        .select('policy_id')
        .in('id', assignmentIds)
        .eq('company_id', profile.company_id);

      if (fetchError) throw fetchError;
      if (!assignments || assignments.length === 0) {
        throw new Error('No assignments found');
      }

      // Soft delete (deactivate) multiple assignments
      const { error } = await supabase
        .from('policy_employee_assignments')
        .update({ 
          is_active: false, 
          end_date: new Date().toISOString().split('T')[0] 
        })
        .in('id', assignmentIds)
        .eq('company_id', profile.company_id);

      if (error) throw error;

      // Clean up fixed accrual release schedules so re-assignment gets fresh initialization
      const { error: releaseCleanupError } = await supabase
        .from('fixed_accrual_releases')
        .delete()
        .in('assignment_id', assignmentIds)
        .eq('company_id', profile.company_id);

      if (releaseCleanupError) {
        console.warn('[leavePolicyService] Failed to clean up fixed accrual releases for assignments:', assignmentIds, releaseCleanupError);
      }
      
      return assignments.map(a => ({ policy_id: a.policy_id }));
    } catch (error) {
      handleLeavePolicyError(error, 'remove policy assignments');
      throw error;
    }
  },

  async getAssignmentsWithProfiles(policyId: string): Promise<(PolicyEmployeeAssignment & { profile: Profile })[]> {
    try {
      // First get the assignments
      const { data: assignments, error: assignmentsError } = await supabase
        .from('policy_employee_assignments')
        .select('*')
        .eq('policy_id', policyId)
        .eq('is_active', true)
        .order('start_date', { ascending: false });

      if (assignmentsError) throw assignmentsError;
      if (!assignments || assignments.length === 0) return [];

      // Then get the profiles for these assignments
      const profileIds = assignments.map(a => a.profile_id);
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .in('id', profileIds);

      if (profilesError) throw profilesError;
      
      // Create a map for quick lookup
      const profileMap = new Map((profiles || []).map(p => [p.id, p]));
      
      // Combine assignments with profiles
      return assignments.map(assignment => ({
        ...assignment,
        profile: profileMap.get(assignment.profile_id) || {
          id: assignment.profile_id,
          first_name: null,
          last_name: null,
          email: 'Unknown'
        } as Profile
      }));
    } catch (error) {
      handleLeavePolicyError(error, 'get assignments with profiles');
      throw error;
    }
  },

  // Balance operations
  // FMEA Fix: Filter balances by active policy assignments to prevent showing
  // balances for unassigned policies. Optional includeInactive for admin audits.
  async getLeaveBalances(profileId?: string, options?: { includeInactive?: boolean; year?: number }): Promise<LeaveBalanceWithProfile[]> {
    try {
      const includeInactive = options?.includeInactive ?? false;
      // FIX: Filter by current year by default to prevent stale year records from appearing
      const balanceYear = options?.year ?? new Date().getFullYear();
      
      let query = supabase
        .from('leave_balances')
        .select('*')
        .eq('balance_year', balanceYear)
        .order('created_at', { ascending: false });

      if (profileId) {
        query = query.eq('profile_id', profileId);
      }

      const { data: balances, error } = await query;
      if (error) throw error;

      if (!balances || balances.length === 0) {
        return [];
      }

      // Get active policy assignments to filter balances
      // This prevents showing balances for policies the user is no longer assigned to
      const profileIds = [...new Set(balances.map(b => b.profile_id))];
      const policyIds = [...new Set(balances.map(b => b.policy_id))];

      const [profilesResult, policiesResult, assignmentsResult] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, first_name, last_name, email')
          .in('id', profileIds),
        supabase
          .from('leave_policies')
          .select('id, name, leave_type')
          .in('id', policyIds),
        // Fetch active assignments to validate which balances should be shown
        supabase
          .from('policy_employee_assignments')
          .select('profile_id, policy_id, is_active')
          .in('profile_id', profileIds)
          .in('policy_id', policyIds)
      ]);

      if (profilesResult.error) throw profilesResult.error;
      if (policiesResult.error) throw policiesResult.error;
      if (assignmentsResult.error) throw assignmentsResult.error;

      const profilesMap = new Map(profilesResult.data?.map(p => [p.id, p]) || []);
      const policiesMap = new Map(policiesResult.data?.map(p => [p.id, p]) || []);
      
      // Create a Set of active profile-policy combinations for O(1) lookup
      const activeAssignments = new Set(
        (assignmentsResult.data || [])
          .filter(a => a.is_active)
          .map(a => `${a.profile_id}:${a.policy_id}`)
      );

      // Filter balances to only include those with active assignments
      // Unless includeInactive is true (for admin audit purposes)
      const filteredBalances = includeInactive 
        ? balances 
        : balances.filter(balance => 
            activeAssignments.has(`${balance.profile_id}:${balance.policy_id}`)
          );

      // Combine the data
      const result: LeaveBalanceWithProfile[] = filteredBalances.map(balance => ({
        ...balance,
        profile: profilesMap.get(balance.profile_id) || { 
          first_name: null, 
          last_name: null, 
          email: 'Unknown' 
        },
        policy: policiesMap.get(balance.policy_id) || { 
          name: 'Unknown Policy', 
          leave_type: 'unknown' 
        }
      }));

      return result;
    } catch (error) {
      handleLeavePolicyError(error, 'get leave balances');
      throw error;
    }
  },

  async updateLeaveBalance(input: UpdateLeaveBalanceInput): Promise<void> {
    try {
      const { data, error } = await supabase.rpc('adjust_leave_balance', {
        p_balance_id: input.balance_id,
        p_adjustment_days: input.adjustment_days ?? null,
        p_adjustment_hours: input.adjustment_hours ?? null,
        p_reason: input.reason || 'Manual adjustment',
      });

      if (error) throw error;
    } catch (error) {
      handleLeavePolicyError(error, 'update leave balance');
      throw error;
    }
  },

  /**
   * Calculate and set initial leave balance for an employee
   * 
   * ⚠️ WARNING: For fixed_accrual policies, this function does NOT grant
   * the initial balance. Fixed accrual uses the scheduled release system
   * (fixed_accrual_releases table) to incrementally grant leave over time.
   * Calling this for fixed accrual will only initialize the tracking schedule
   * and set balance to 0 to prevent double-counting.
   * 
   * @param policyId - The leave policy ID
   * @param profileId - The employee profile ID
   */
  async calculateInitialBalance(policyId: string, profileId: string): Promise<void> {
    try {
      const { data: policy } = await supabase
        .from('leave_policies')
        .select(`
          *,
          accrual_rule:leave_accrual_rules!policy_id(*)
        `)
        .eq('id', policyId)
        .single();

      if (!policy) throw new Error('Policy not found');
      
      const accrualRule = Array.isArray(policy.accrual_rule) ? policy.accrual_rule[0] : policy.accrual_rule;
      if (!accrualRule) throw new Error('Accrual rule not found');

      // Get employee profile for pro-rata and tenure calculations
      const { data: profile } = await supabase
        .from('profiles')
        .select('created_at, company_id')
        .eq('id', profileId)
        .single();

      if (!profile) throw new Error('Employee profile not found');

      const currentYear = new Date().getFullYear();
      const currentDate = new Date();
      const employeeStartDate = new Date(profile.created_at);
      
      // Calculate initial balance based on accrual method
      let initialDays = 0;
      let initialHours = 0;

      switch (policy.accrual_method) {
        case 'does_not_accrue':
          // No accrual - balance remains 0
          initialDays = 0;
          initialHours = 0;
          break;

        case 'fixed_accrual':
          // For fixed accrual, we need to set annual_entitlement to the full policy amount
          // but current_balance starts at 0 (will be incremented by releases)
          const { data: policyRules } = await supabase
            .from('leave_accrual_rules')
            .select('fixed_accrual_amount, fixed_accrual_unit')
            .eq('policy_id', policyId)
            .single();
            
          if (policyRules) {
            if (policyRules.fixed_accrual_unit === 'hours') {
              initialHours = policyRules.fixed_accrual_amount;
              initialDays = policyRules.fixed_accrual_amount / 8;
            } else {
              initialDays = policyRules.fixed_accrual_amount;
              initialHours = policyRules.fixed_accrual_amount * 8;
            }
          }
          
          // Ensure tracking schedule is initialized
          const { data: assignment } = await supabase
            .from('policy_employee_assignments')
            .select('id')
            .eq('profile_id', profileId)
            .eq('policy_id', policyId)
            .eq('is_active', true)
            .maybeSingle();
          
          if (assignment) {
            // NOTE: initialize_fixed_accrual_schedule is idempotent - safe to call multiple times
            // It checks if releases have been processed and returns early if already initialized
            await supabase.rpc('initialize_fixed_accrual_schedule', {
              p_profile_id: profileId,
              p_policy_id: policyId,
              p_assignment_id: assignment.id
            });
          }
          break;

        case 'hourly_worked':
          // For hourly worked, we start with 0 and accrue based on hours worked
          if (accrualRule.hourly_accrual_type === 'per_hour_worked') {
            // Start with 0, leave accrues as hours are worked
            initialHours = 0;
            initialDays = 0;
          } else if (accrualRule.hourly_accrual_type === 'per_threshold_worked') {
            // Start with 0, leave is released when thresholds are met
            initialHours = 0;
            initialDays = 0;
          }
          break;

        default:
          console.warn(`Unknown accrual method: ${policy.accrual_method}`);
          break;
      }

      // Apply pro-rata calculation if enabled and employee started mid-year
      // For fixed accrual, use fixed_accrual_pro_rata; for others, use enable_pro_rata
      const shouldApplyProRata = policy.accrual_method === 'fixed_accrual'
        ? accrualRule.fixed_accrual_pro_rata
        : accrualRule.enable_pro_rata;

      // Store the full annual entitlement before pro-rating
      const fullAnnualDays = initialDays;
      const fullAnnualHours = initialHours;

      // For fixed_accrual policies, skip pro-rating the annual entitlement
      // because the SQL scheduler already handles the pro-rated release correctly using day-based math
      const isFixedAccrual = policy.accrual_method === 'fixed_accrual';

      if (shouldApplyProRata && employeeStartDate.getFullYear() === currentYear && !isFixedAccrual) {
        const startOfYear = new Date(currentYear, 0, 1);
        if (employeeStartDate > startOfYear) {
          const monthsRemaining = 12 - employeeStartDate.getMonth();
          const proRataMultiplier = monthsRemaining / 12;
          
          initialDays = initialDays * proRataMultiplier;
          initialHours = initialHours * proRataMultiplier;
          
          // Apply rounding if specified
          if (accrualRule.pro_rata_rounding_direction === 'up') {
            initialDays = Math.ceil(initialDays);
            initialHours = Math.ceil(initialHours);
          } else if (accrualRule.pro_rata_rounding_direction === 'down') {
            initialDays = Math.floor(initialDays);
            initialHours = Math.floor(initialHours);
          } else if (accrualRule.pro_rata_rounding_direction === 'nearest') {
            initialDays = Math.round(initialDays);
            initialHours = Math.round(initialHours);
          }
        }
      }

      // Apply max yearly accrual limits if enabled
      if (accrualRule.enable_max_yearly_accrual && accrualRule.max_yearly_accrual_days) {
        initialDays = Math.min(initialDays, accrualRule.max_yearly_accrual_days);
      }

      // Apply balance caps if set
      if (accrualRule.balance_cap_days && initialDays > accrualRule.balance_cap_days) {
        initialDays = accrualRule.balance_cap_days;
      }
      if (accrualRule.balance_cap_hours && initialHours > accrualRule.balance_cap_hours) {
        initialHours = accrualRule.balance_cap_hours;
      }

      // Create or update balance record
      const { error } = await supabase
        .from('leave_balances')
        .upsert({
          profile_id: profileId,
          policy_id: policyId,
          company_id: policy.company_id,
          // For fixed accrual: current balance stays at 0 initially (releases will increment it)
          current_balance_days: policy.accrual_method === 'fixed_accrual' ? 0 : initialDays,
          current_balance_hours: policy.accrual_method === 'fixed_accrual' ? 0 : initialHours,
          // For fixed_accrual policies, always use full annual amount (SQL scheduler handles pro-rated releases)
          // For other accrual methods, use potentially pro-rated amount
          annual_entitlement_days: isFixedAccrual ? fullAnnualDays : initialDays,
          annual_entitlement_hours: isFixedAccrual ? fullAnnualHours : initialHours,
          balance_year: currentYear,
          last_calculated_at: new Date().toISOString()
        });

      if (error) throw error;
    } catch (error) {
      handleLeavePolicyError(error, 'calculate initial balance');
      throw error;
    }
  }
};